import { useState } from 'react'
import { Copy, Check, ExternalLink, Eye } from 'lucide-react'
import { getFullProfileUrl } from '@/utils/profileUrl'
import { useTranslation } from 'react-i18next'

interface ShareToolsProps {
  username?: string
}

export default function ShareTools({ username }: ShareToolsProps) {
  const { t } = useTranslation('profile')
  const [copied, setCopied] = useState(false)

  const profileUrl = username ? getFullProfileUrl(username) : ''

  const handleCopy = async () => {
    if (!profileUrl) return

    try {
      await navigator.clipboard.writeText(profileUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const handleOpenProfile = () => {
    if (!profileUrl) return
    window.open(profileUrl, '_blank', 'noopener,noreferrer')
  }

  if (!username) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('sharing.shareTools.title')}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('sharing.shareTools.noUsername')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t('sharing.shareTools.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('sharing.shareTools.copyDescription')}
        </p>
      </div>

      {/* Profile URL display */}
      <div className="flex gap-2">
        <input
          type="text"
          value={profileUrl}
          readOnly
          className="flex-grow px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm text-gray-700 cursor-text"
          onClick={(e) => e.currentTarget.select()}
        />

        <button
          onClick={handleCopy}
          className="btn-secondary inline-flex items-center gap-2"
          aria-label="Copy profile URL"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-green-600" />
              {t('sharing.shareTools.copied')}
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              {t('sharing.shareTools.copy')}
            </>
          )}
        </button>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleOpenProfile}
          className="btn-primary inline-flex items-center justify-center gap-2"
        >
          <Eye className="w-4 h-4" />
          {t('sharing.shareTools.previewProfile')}
        </button>

        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary inline-flex items-center justify-center gap-2"
        >
          <ExternalLink className="w-4 h-4" />
          {t('sharing.shareTools.openInNewTab')}
        </a>
      </div>

      {/* Tips */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm font-medium text-gray-700 mb-2">
          {t('sharing.shareTools.tipsTitle')}
        </p>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>{t('sharing.shareTools.tip1')}</li>
          <li>{t('sharing.shareTools.tip2')}</li>
          <li>{t('sharing.shareTools.tip3')}</li>
          <li>{t('sharing.shareTools.tip4')}</li>
        </ul>
      </div>
    </div>
  )
}
